<script setup lang="ts">
import { computed, nextTick, ref, watch } from "vue";
import type { CustomFilterProps, SelectFilterItem, SelectFilterOptions } from "./types";

const props = withDefaults(defineProps<CustomFilterProps>(), {
	size: "middle",
	labelWidth: 80,
	labelAlign: "right",
	resetText: "重置",
	searchText: "查询",
	filterList: () => [],
});

const labelStyle = computed(() => {
	return {
		width: typeof props.labelWidth === "number" ? `${props.labelWidth}px` : props.labelWidth,
		textAlign: props.labelAlign,
	};
});

const value = defineModel<AObject>("value", { default: () => ({}) });
const remoteOptionsMap = ref<Record<string, SelectFilterOptions>>({});
const remoteLoadingMap = ref<Record<string, boolean>>({});
const hasRequestedInitComplete = ref(false);
const hasEmittedInitComplete = ref(false);
const isRemoteLoading = computed(() => Object.values(remoteLoadingMap.value).some(Boolean));

/**
 * 获取当前下拉项依赖的字段列表。
 */
const getDependsOnFields = (item: SelectFilterItem) => {
	if (!item.dependsOn) {
		return [] as string[];
	}

	return Array.isArray(item.dependsOn) ? item.dependsOn : [item.dependsOn];
};

/**
 * 读取下拉选项的 value 字段。
 */
const getOptionValue = (option: unknown) => {
	if (typeof option !== "object" || option === null) {
		return undefined;
	}

	return (option as { value?: unknown }).value;
};

/**
 * 按配置自动选择第一项。
 * force=true 时会强制覆盖当前值。
 */
const applyAutoSelectFirst = (
	item: SelectFilterItem,
	options: SelectFilterOptions,
	force = false
) => {
	if (!item.autoSelectFirst) {
		return;
	}

	const optionList = (options ?? []) as unknown[];
	const firstValue = getOptionValue(optionList[0]);
	const currentValue = value.value[item.field];

	if (optionList.length === 0) {
		value.value[item.field] = undefined;

		return;
	}

	const hasCurrentValue = optionList.some((option) => getOptionValue(option) === currentValue);

	if (force || !hasCurrentValue) {
		value.value[item.field] = firstValue;
	}
};

/**
 * 加载单个下拉的远程选项，并处理 loading 与默认值。
 */
const loadSingleSelectRemoteData = async (item: SelectFilterItem, forceSelectFirst = false) => {
	if (!item.loadRemoteData) {
		return;
	}

	remoteLoadingMap.value[item.field] = true;

	try {
		const data =
			typeof item.loadRemoteData === "function"
				? await item.loadRemoteData(value.value)
				: await item.loadRemoteData;

		const options = (data ?? []) as SelectFilterOptions;

		remoteOptionsMap.value[item.field] = options;
		applyAutoSelectFirst(item, options, forceSelectFirst);
	} catch {
		remoteOptionsMap.value[item.field] = [];
		value.value[item.field] = undefined;
	} finally {
		remoteLoadingMap.value[item.field] = false;
	}
};

/**
 * 获取下拉选项：远程数据优先，回退本地 options。
 */
const getSelectOptions = (field: string, localOptions?: SelectFilterOptions) => {
	return remoteOptionsMap.value[field] ?? localOptions ?? [];
};

/**
 * 获取下拉远程加载状态。
 */
const getSelectLoading = (field: string) => {
	return Boolean(remoteLoadingMap.value[field]);
};

/**
 * 初始化加载所有 select 的远程数据。
 */
const loadSelectRemoteData = async () => {
	const selectItems = props.filterList.filter(
		(item) => item.type === "select"
	) as SelectFilterItem[];

	await Promise.all(selectItems.map(async (item) => loadSingleSelectRemoteData(item)));
};

/**
 * 标记组件已经开始初始化，并在所有远程数据加载完成后回调外部。
 */
watch(
	() => props.filterList,
	async () => {
		hasRequestedInitComplete.value = true;

		void loadSelectRemoteData();

		await nextTick();
	},
	{ immediate: true, deep: true }
);

watch(
	() => [hasRequestedInitComplete.value, isRemoteLoading.value],
	async ([hasRequestedInitRequested, loading]) => {
		if (!hasRequestedInitRequested || loading || hasEmittedInitComplete.value) {
			return;
		}

		await nextTick();

		if (isRemoteLoading.value || hasEmittedInitComplete.value) {
			return;
		}

		hasEmittedInitComplete.value = true;
		emits("dataInitComplete", { ...value.value });
	}
);

/**
 * 生成依赖字段快照，用于精确检测联动字段变化。
 */
const getDependencySnapshot = () => {
	const selectItems = props.filterList.filter(
		(item) => item.type === "select"
	) as SelectFilterItem[];
	const snapshot: Record<string, unknown[]> = {};

	selectItems.forEach((item) => {
		const dependsOnFields = getDependsOnFields(item);

		if (dependsOnFields.length === 0) {
			return;
		}

		snapshot[item.field] = dependsOnFields.map((field) => value.value[field]);
	});

	return snapshot;
};

watch(getDependencySnapshot, (newSnapshot, oldSnapshot) => {
	const selectItems = props.filterList.filter(
		(item) => item.type === "select"
	) as SelectFilterItem[];

	selectItems.forEach((item) => {
		const dependsOnFields = getDependsOnFields(item);

		if (dependsOnFields.length === 0) {
			return;
		}

		const newValues = newSnapshot[item.field] ?? [];
		const oldValues = oldSnapshot?.[item.field] ?? [];
		const dependencyChanged = dependsOnFields.some((_, index) => {
			return !Object.is(newValues[index], oldValues[index]);
		});

		if (!dependencyChanged) {
			return;
		}

		remoteOptionsMap.value[item.field] = [];
		value.value[item.field] = undefined;

		void loadSingleSelectRemoteData(item, true);
	});
});

const emits = defineEmits<{
	reset: [];
	search: [value: AObject];
	dataInitComplete: [value: AObject];
}>();

const handleReset = () => {
	value.value = {};
	emits("reset");
};

const handleSearch = () => {
	emits("search", value.value);
};
</script>

<template>
	<div class="gap-x-hs gap-y-mm grid grid-cols-[repeat(auto-fill,minmax(320px,1fr))]">
		<template v-for="(item, index) in filterList" :key="index">
			<div class="gap-smm flex w-full items-center">
				<div class="line-clamp-1 shrink-0" :style="labelStyle">
					{{ item.label }}
				</div>
				<template v-if="item.type === 'input'">
					<a-input
						v-model:value="value[item.field]"
						class="!w-full"
						:size="size"
						:placeholder="item.placeholder || `请输入${item.label}`"
						v-bind="item.props"
					></a-input>
				</template>
				<template v-else-if="item.type === 'select'">
					<a-select
						v-model:value="value[item.field]"
						class="!w-full"
						:size="size"
						:placeholder="item.placeholder || `请选择${item.label}`"
						:options="getSelectOptions(item.field, item.options)"
						:loading="getSelectLoading(item.field)"
						:allow-clear="true"
						v-bind="item.props"
					></a-select>
				</template>
				<template v-else-if="item.type === 'datePicker'">
					<a-date-picker
						v-model:value="value[item.field]"
						class="!w-full"
						:size="size"
						:placeholder="item.placeholder || `请选择${item.label}`"
						:v-bind="item.props"
					></a-date-picker>
				</template>
				<template v-else-if="item.type === 'rangePicker'">
					<a-range-picker
						v-model:value="value[item.field]"
						:v-bind="item.props"
						class="!w-full"
						:size="size"
						:placeholder="
							item.placeholder || [`请选择${item.label}开始日期`, `请选择${item.label}结束日期`]
						"
					></a-range-picker>
				</template>
			</div>
		</template>

		<div class="gap-smm col-start-[-2] flex items-center justify-end justify-self-end">
			<a-button :size="size" @click="handleReset">重置</a-button>
			<a-button :size="size" type="primary" @click="handleSearch">查询</a-button>
		</div>
	</div>
</template>
